// Read-only display for a signed structured note.

function FieldValue({ field, value }) {
  if (value === undefined || value === null || value === '') return null;

  if (field.type === 'array') {
    if (!Array.isArray(value) || value.length === 0) return null;
    return (
      <div>
        <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">
          {field.label}
        </p>
        <div className="space-y-3">
          {value.map((item, idx) => (
            <div
              key={idx}
              className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-3"
            >
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                {field.itemLabel} {idx + 1}
              </p>
              <dl className="space-y-2">
                {field.itemFields.map((itemField) => {
                  const itemVal = item[itemField.key];
                  if (!itemVal && itemVal !== 0) return null;
                  const displayVal = resolveDisplayValue(itemField, itemVal);
                  if (!displayVal) return null;
                  return (
                    <div key={itemField.key}>
                      <dt className="text-xs text-neutral-500 dark:text-neutral-400">{itemField.label}</dt>
                      <dd className="text-sm text-neutral-900 dark:text-neutral-100 mt-0.5 whitespace-pre-wrap">{displayVal}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const display = resolveDisplayValue(field, value);
  if (!display) return null;

  return (
    <div>
      <dt className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
        {field.label}
      </dt>
      <dd className="mt-1 text-sm text-neutral-900 dark:text-neutral-100 whitespace-pre-wrap">{display}</dd>
    </div>
  );
}

function resolveDisplayValue(field, value) {
  if (field.type === 'select') {
    const opt = field.options?.find((o) => o.value === value);
    const label = opt?.label;
    if (!label || label === '— Select —') return null;
    return label;
  }
  if (field.type === 'checkboxes') {
    if (!Array.isArray(value) || value.length === 0) return null;
    return value
      .map((v) => field.options?.find((o) => o.value === v)?.label ?? v)
      .join(', ');
  }
  return value !== null && value !== undefined && value !== '' ? String(value) : null;
}

function SectionViewer({ section, data }) {
  // Only render section if at least one field has a value.
  const hasContent = section.fields.some((f) => {
    const val = data[f.key];
    if (f.type === 'array') return Array.isArray(val) && val.length > 0;
    if (f.type === 'checkboxes') return Array.isArray(val) && val.length > 0;
    return val !== undefined && val !== null && val !== '';
  });

  if (!hasContent) return null;

  return (
    <div>
      <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4 pb-2 border-b border-neutral-200 dark:border-neutral-700">
        {section.title}
      </h3>
      <dl className="space-y-3">
        {section.fields.map((field) => (
          <FieldValue key={field.key} field={field} value={data[field.key]} />
        ))}
      </dl>
    </div>
  );
}

/**
 * Read-only structured note viewer for signed/locked notes.
 *
 * Props:
 *   config – noteTypeConfig object
 *   data   – parsed structured data object (without _format key)
 */
export default function StructuredNoteViewer({ config, data = {} }) {
  return (
    <div className="space-y-8">
      {config.sections.map((section, idx) => (
        <SectionViewer key={idx} section={section} data={data} />
      ))}
    </div>
  );
}
